import { HStack, PageContainer, PageHeader } from '@/components/Atoms'
import { DeleteAlertDialog } from '@/components/AlertDialog/PresetAlerts/DeleteAlertDialog'
import { Badge } from '@/components/Badge'
import { Card, CardContent } from '@/components/Card'
import { DataTable, DataTableRow } from '@/components/DataTable'
import { TableHeader } from '@/components/DataTable/CellContent/TableHeader'
import { NewDropdown } from '@/components/NewDropdown'
import type { NewDropdownOption } from '@/components/NewDropdown/types'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  AzureImports,
  azureImportsSearchSchema,
  useAzureImports,
  useAzureImportSettings,
  useDeleteAzureImport,
  useRetryAzureImport,
} from '@/hooks/queries/uploads/useAzureImports'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { useTablePagination } from '@/hooks/useTablePagination'
import { getInitialColumnVisibility } from '@/lib/utils/datatable'
import { getClearedFilters, useAreFiltersApplied, useSelectedFilters } from '@/lib/utils/filters'
import { DATATABLE_NAMES } from '@/stores/datatable'
import {
  AZURE_IMPORT_STATUS,
  AZURE_IMPORT_STATUS_VALUES,
  AzureImportStatus,
  RETRYABLE_AZURE_IMPORT_STATUSES,
} from '@repo/constants'
import { AZURE_IMPORTS_TYPES, UploadScope } from '@repo/csv'
import { hasPermissions } from '@repo/permissions'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Separator,
} from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { ColumnDef, ColumnSort } from '@tanstack/react-table'
import {
  EllipsisVerticalIcon,
  FileTextIcon,
  RefreshCcwIcon,
  SettingsIcon,
  Trash2Icon,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AzureImportSettingsDialog } from './-components/AzureImportSettingsDialog'

export const Route = createFileRoute('/_auth/_modules/crt/azure-imports/')({
  component: AzureImportsPage,
  validateSearch: (search) => azureImportsSearchSchema.parse(search),
})

type AzureImportRow = AzureImports['azureImports'][number] & DataTableRow

const STATUS_TONES: Record<AzureImportStatus, 'info' | 'warning' | 'success' | 'critical'> = {
  DETECTED: 'info',
  PROCESSING: 'warning',
  PROCESSED: 'critical',
  COMPLETED: 'success',
  FAILED: 'critical',
  FILE_MISSING: 'critical',
} as const

function AzureImportsPage() {
  const { t } = useTranslation(['uploads', 'common'])
  const { formatDateWithTime, formatDateLong } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({
    navigate,
    search,
  })

  const {
    isPending,
    data: { azureImports, count } = { azureImports: [], count: 0 },
    error,
  } = useAzureImports('crt', { ...search, page: search.page ?? 1, pageSize })

  const retryMutation = useRetryAzureImport('crt')

  const { permissions } = usePermissions()
  const canConfigure = hasPermissions(permissions, 'crt_azure_imports_manage')
  const { data: settings } = useAzureImportSettings('crt')
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  const canDelete = hasPermissions(permissions, 'crt_azure_imports_delete')
  const deleteMutation = useDeleteAzureImport('crt')
  const [importToDelete, setImportToDelete] = useState<AzureImportRow | null>(null)

  const [uploadTypes, setUploadTypes] = useState<string[]>(search.type ?? [])
  const [statuses, setStatuses] = useState<string[]>(search.status ?? [])

  const uploadTypeOptions = Object.keys(AZURE_IMPORTS_TYPES)
    .map((key) => {
      return {
        value: key,
        label: t(`uploadType.${key}`),
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label)) as NewDropdownOption[]

  const selectedFilters = useSelectedFilters({ type: uploadTypes, status: statuses })
  const areFiltersApplied = useAreFiltersApplied(selectedFilters)

  const onPageChange = (page: number) => {
    navigate({ search: { ...search, page: page + 1 } })
  }

  const onSortChange = (sort: ColumnSort) => {
    navigate({ search: { ...search, sort } })
  }

  const showResults = () => {
    const filteredData = selectedFilters
      ? Object.fromEntries(
          Object.entries(selectedFilters).filter(([, value]) => value !== undefined),
        )
      : undefined

    navigate({ search: { ...search, ...filteredData, page: 1 } })
  }

  const resetAllFilters = () => {
    setUploadTypes([])
    setStatuses([])
    const clearedFilters = getClearedFilters(selectedFilters)

    navigate({ search: { ...search, ...clearedFilters, page: 1 } })
  }

  const statusOptions = AZURE_IMPORT_STATUS_VALUES.map((status) => ({
    value: status,
    label: t(`azureImports.status.${status}`),
  }))

  const columns: ColumnDef<AzureImportRow>[] = [
    {
      id: 'uploadType',
      accessorKey: 'uploadType',
      header: ({ column }) => <TableHeader column={column} />,
      cell: ({ row }) => (
        <Badge className="w-fit">{t(`uploadType.${row.original.uploadType}`)}</Badge>
      ),
      enableHiding: false,
      meta: {
        displayTitle: t('azureImports.table.columns.uploadType'),
        cellClassName: 'truncate',
      },
    },
    {
      id: 'blobId',
      accessorKey: 'blobId',
      header: ({ column }) => <TableHeader column={column} />,
      cell: ({ row }) => row.original.blobId,
      enableHiding: false,
      meta: {
        displayTitle: t('azureImports.table.columns.blobId'),
        cellClassName: 'truncate',
      },
    },
    {
      id: 'blobCreatedAt',
      accessorKey: 'blobCreatedAt',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) =>
        row.original.blobCreatedAt ? formatDateLong(new Date(row.original.blobCreatedAt)) : '-',
      meta: {
        displayTitle: t('azureImports.table.columns.createdAt'),
        cellClassName: 'truncate',
      },
    },
    {
      id: 'blobDetectedAt',
      accessorKey: 'blobDetectedAt',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => formatDateWithTime(new Date(row.original.blobDetectedAt)),
      meta: {
        displayTitle: t('azureImports.table.columns.importedAt'),
        cellClassName: 'truncate',
      },
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <TableHeader column={column} />,
      cell: ({ row }) => (
        <Badge
          tone={STATUS_TONES[row.original.status as AzureImportStatus] ?? 'default'}
          className="w-fit"
        >
          {t(`azureImports.status.${row.original.status}`)}
        </Badge>
      ),
      meta: {
        displayTitle: t('azureImports.table.columns.status'),
        cellClassName: 'truncate',
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const isRetryable = RETRYABLE_AZURE_IMPORT_STATUSES.has(
          row.original.status as AzureImportStatus,
        )
        const hasUploadDetails = !!row.original.uploadId
        const isRetryPending =
          retryMutation.isPending && retryMutation.variables === row.original.id
        const isDeletable = canDelete && row.original.status !== AZURE_IMPORT_STATUS.PROCESSING
        const hasActions = isRetryable || hasUploadDetails || isDeletable

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" disabled={!hasActions}>
                <EllipsisVerticalIcon className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() =>
                  navigate({
                    to: '/crt/my-uploads/$scope/$fileName',
                    params: {
                      scope: (row.original.scope ?? 'app') as UploadScope,
                      fileName: row.original.fileName ?? '',
                    },
                  })
                }
                disabled={!hasUploadDetails}
                className="cursor-pointer"
              >
                <FileTextIcon className="size-4" />
                {t('azureImports.actions.viewReport')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => retryMutation.mutate(row.original.id)}
                disabled={!isRetryable || isRetryPending}
                className="cursor-pointer"
              >
                <RefreshCcwIcon className="size-4" />
                {t('azureImports.actions.retry')}
              </DropdownMenuItem>
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => setImportToDelete(row.original)}
                  disabled={!isDeletable}
                  className="text-destructive cursor-pointer"
                >
                  <Trash2Icon className="size-4" />
                  {t('azureImports.actions.delete')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      meta: {
        hideInColumnsMenu: true,
        cellClassName: 'w-0',
      },
    },
  ]

  const initialColumnVisibility = getInitialColumnVisibility(columns)

  return (
    <PageContainer>
      <HStack gap="sm" className="items-center justify-between">
        <PageHeader>{t('route.azureImports', { ns: 'common' })}</PageHeader>
        <HStack gap="sm" className="items-center">
          {settings && (
            <Badge tone={settings.startDate ? 'info' : 'warning'}>
              {settings.startDate
                ? t('azureImports.settings.pullingFrom', {
                    date: formatDateLong(settings.startDate),
                  })
                : t('azureImports.settings.notConfigured')}
            </Badge>
          )}
          {canConfigure && (
            <Button
              type="button"
              variant="secondary"
              disabled={!settings}
              onClick={() => setIsSettingsOpen(true)}
            >
              <SettingsIcon className="size-4" />
              {t('azureImports.settings.configure')}
            </Button>
          )}
        </HStack>
      </HStack>

      <DeleteAlertDialog
        title={t('azureImports.delete.title')}
        description={t('azureImports.delete.description', { fileName: importToDelete?.blobId })}
        warning={t('azureImports.delete.warning')}
        isButtonDestructive
        open={!!importToDelete}
        onOpenChange={(isOpen) => !isOpen && setImportToDelete(null)}
        onDelete={() => importToDelete && deleteMutation.mutate(importToDelete.id)}
        isPending={deleteMutation.isPending}
      />

      <AzureImportSettingsDialog
        module="crt"
        isOpen={isSettingsOpen}
        onOpenChange={setIsSettingsOpen}
        startDate={settings?.startDate ?? null}
      />

      <Card>
        <CardContent className="py-6">
          <HStack gap="sm" className="w-full min-w-0 flex-nowrap">
            <div className="flex w-full gap-2">
              <div className="w-full max-w-[300px]">
                <NewDropdown
                  placeholder={t('azureImports.filters.selectUploadType')}
                  options={uploadTypeOptions}
                  value={uploadTypes}
                  onChange={setUploadTypes}
                  selectionMode="multiple"
                  searchable
                />
              </div>
              <div className="w-full max-w-[300px]">
                <NewDropdown
                  placeholder={t('azureImports.filters.selectStatus')}
                  options={statusOptions}
                  value={statuses}
                  onChange={setStatuses}
                  selectionMode="multiple"
                  searchable
                />
              </div>
            </div>

            <Button
              variant="secondary"
              className="flex-none"
              disabled={!areFiltersApplied}
              onClick={resetAllFilters}
            >
              {t('actions.clear', { ns: 'common' })}
            </Button>

            <Button onClick={showResults} className="flex-none" disabled={isPending}>
              <RefreshCcwIcon className="size-4" />
              {t('actions.filters.apply', { ns: 'common' })}
            </Button>
          </HStack>

          <Separator />

          <DataTable
            name={DATATABLE_NAMES.AZURE_IMPORTS}
            columns={columns}
            initialColumnVisibility={initialColumnVisibility}
            data={azureImports as unknown as AzureImportRow[]}
            isPending={isPending}
            error={error}
            page={search.page}
            pageSize={pageSize}
            rowCount={count}
            sort={search.sort}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onShowResults={showResults}
            onSortChange={onSortChange}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
